import React, { memo } from 'react';
import { PUBLICO_ALVO } from '@investirapri/financa.js/lib/enums';
import { formats, validators } from 'investira.sdk';
import { Box, Card, Divider, Icon, Stack, Tooltip, Typography } from 'investira.react.components';

const wDiasTipoDescription = {
  U: {
    singular: 'útil',
    plural: 'úteis'
  },
  C: {
    singular: 'corrido',
    plural: 'corridos'
  }
};

/**
 * Localiza a descrição do público-alvo informado pelo fundo.
 *
 * @param {string|number} pPublicoAlvoId Identificador do público-alvo.
 * @returns {string|null}
 */
function pvGetPublicoAlvoDescription(pPublicoAlvoId) {
  const xPublicoAlvo = Object.values(PUBLICO_ALVO).find(
    pItem => String(pItem.id) === String(pPublicoAlvoId)
  );

  return xPublicoAlvo?.descricao || null;
}

/**
 * Formata um valor percentual preservando o valor zero.
 *
 * @param {number|string} pValue Valor percentual.
 * @returns {string}
 */
function pvFormatPercentual(pValue) {
  return `${formats.formatNumber(pValue, 2, true, false)}%`;
}

/**
 * Formata a aplicação mínima, usando o placeholder quando o fundo informa zero.
 *
 * @param {number|string} pValue Valor da aplicação mínima.
 * @returns {string}
 */
function pvFormatAplicacaoMinima(pValue) {
  if (Number(pValue) === 0) {
    return '---';
  }

  return formats.friendlyNumber(pValue, 2, true);
}

/**
 * Formata o prazo de resgate com a contagem de dias informada.
 *
 * @param {number|string} pDias Quantidade de dias do prazo.
 * @param {string} pDiasTipo Tipo de contagem dos dias.
 * @returns {string}
 */
function pvFormatPrazoResgate(pDias, pDiasTipo) {
  const xDias = Number(pDias);

  if (xDias === 0) {
    return 'Mesmo dia';
  }

  const xDiasTipo = wDiasTipoDescription[pDiasTipo];
  const xDiasTipoDescription = xDiasTipo
    ? ` ${xDias === 1 ? xDiasTipo.singular : xDiasTipo.plural}`
    : '';

  return `${pDias} ${xDias === 1 ? 'dia' : 'dias'}${xDiasTipoDescription}`;
}

/**
 * Explica o prazo técnico usado para calcular o valor do resgate.
 *
 * @param {number|string} pDias Quantidade de dias até a cotização.
 * @returns {string|null}
 */
function pvGetCotizacaoTooltip(pDias) {
  if (validators.isNull(pDias)) {
    return null;
  }

  if (Number(pDias) === 0) {
    return 'Cotização D+0: o valor do resgate é calculado no mesmo dia considerado pelo fundo.';
  }

  return `Cotização D+${pDias}: o valor do resgate é calculado ${pvFormatPrazoResgate(
    pDias
  )} após o pedido.`;
}

/**
 * Explica o prazo técnico usado para disponibilizar o pagamento do resgate.
 *
 * @param {number|string} pDias Quantidade de dias após a cotização.
 * @param {string} pDiasTipo Tipo de contagem dos dias.
 * @returns {string|null}
 */
function pvGetLiquidacaoTooltip(pDias, pDiasTipo) {
  if (validators.isNull(pDias)) {
    return null;
  }

  const xDias = Number(pDias);

  if (xDias === 0) {
    return 'Liquidação D+0: o pagamento fica disponível no mesmo dia da cotização.';
  }

  const xDiasTipo = wDiasTipoDescription[pDiasTipo];
  const xDiasTipoDescription = xDiasTipo
    ? ` ${xDias === 1 ? xDiasTipo.singular : xDiasTipo.plural}`
    : '';

  return `Liquidação D+${pDias}: o pagamento fica disponível ${pvFormatPrazoResgate(
    pDias
  )}${xDiasTipoDescription} após a cotização.`;
}

/**
 * Renderiza uma linha de informações preservando a ordem recebida.
 *
 * @param {Array<Object>} pItems Informações exibidas na linha.
 * @returns {JSX.Element|null}
 */
function pvRenderItems(pItems) {
  if (validators.isEmpty(pItems)) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(3, minmax(0, 1fr))'
        },
        rowGap: 2
      }}>
      {pItems.map((pItem, pIndex) => (
        <Stack
          key={pItem.id}
          sx={{
            minWidth: 0,
            position: 'relative',
            pt: {
              xs: pIndex > 0 ? 2 : 0,
              sm: 0
            },
            pl: {
              xs: 0,
              sm: pIndex > 0 ? 2 : 0
            }
          }}>
          {pIndex > 0 && (
            <>
              <Divider
                sx={{
                  display: {
                    xs: 'block',
                    sm: 'none'
                  },
                  left: 0,
                  position: 'absolute',
                  top: 0,
                  width: '100%'
                }}
              />
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  bottom: 0,
                  display: {
                    xs: 'none',
                    sm: 'block'
                  },
                  left: 0,
                  position: 'absolute',
                  top: 0
                }}
              />
            </>
          )}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" color="textPrimary" sx={{ fontWeight: 500 }}>
              {validators.isNull(pItem.value) ? '---' : pItem.formatValue(pItem.value)}
            </Typography>
            {pItem.tooltip && !validators.isNull(pItem.value) && (
              <Tooltip title={pItem.tooltip} arrow>
                <Box
                  component="span"
                  sx={{
                    width: 16,
                    height: 16,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                  <Icon iconName="information" size={16} color="primary" />
                </Box>
              </Tooltip>
            )}
          </Stack>
          <Typography variant="caption" color="textSecondary">
            {pItem.label}
          </Typography>
        </Stack>
      ))}
    </Box>
  );
}

/**
 * Apresenta as principais condições de acesso e custos de um fundo.
 *
 * @param {Object} pProps Propriedades do componente.
 * @param {Object} pProps.data Dados cadastrais do fundo.
 * @returns {JSX.Element}
 */
const FundoInfoCard = memo(pProps => {
  const { data = {} } = pProps;
  const xPublicoAlvo = pvGetPublicoAlvoDescription(data.publico_alvo);
  const xCotizacao = data.cotizacao || {};
  const xResgate = data.resgate || {};
  const xAcessoItems = [
    {
      id: 'aplicacao-minima',
      label: 'Aplicação mínima',
      value: data.aplicacao_min,
      formatValue: pvFormatAplicacaoMinima
    },
    {
      id: 'cotizacao-resgate',
      label: 'Cálculo do resgate',
      value: xCotizacao.dias_resgate,
      formatValue: pvFormatPrazoResgate,
      tooltip: pvGetCotizacaoTooltip(xCotizacao.dias_resgate)
    },
    {
      id: 'liquidacao-resgate',
      label: 'Prazo para resgate',
      value: xResgate.dias_pagamento,
      formatValue: pValue => pvFormatPrazoResgate(pValue, xResgate.dias_tipo),
      tooltip: pvGetLiquidacaoTooltip(xResgate.dias_pagamento, xResgate.dias_tipo)
    }
  ];
  const xTaxaItems = [
    {
      id: 'taxa-administracao',
      label: 'Taxa de administração',
      value: data.taxas?.adm,
      formatValue: pvFormatPercentual
    },
    {
      id: 'taxa-performance',
      label: 'Taxa de performance',
      value: data.performance?.taxa,
      formatValue: pvFormatPercentual
    }
  ];

  return (
    <Card
      sx={{
        flex: 1,
        minHeight: 0
      }}>
      <Stack px={2} py={2} spacing={2} height="100%" justifyContent="space-between">
        <Stack>
          <Typography variant="caption" color="textPrimary" sx={{ fontWeight: 500 }}>
            Público alvo
          </Typography>
          <Typography variant="h6" color="textPrimary" sx={{ fontWeight: 500 }}>
            {xPublicoAlvo || '---'}
          </Typography>
        </Stack>

        {pvRenderItems(xAcessoItems)}
        <Divider
          sx={{
            display: {
              xs: 'block',
              sm: 'none'
            }
          }}
        />
        {pvRenderItems(xTaxaItems)}
      </Stack>
    </Card>
  );
});

FundoInfoCard.displayName = 'FundoInfoCard';

export default FundoInfoCard;
